import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, MapPin, Check } from "lucide-react";
import { PageHero } from "../components/site/Blocks";
import { Reveal } from "../components/site/Motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CONTACT, SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", research_type: "", budget: "", geography: "", message: "" };
const BUDGETS = ["Under $5k", "$5k – $20k", "$20k – $50k", "$50k+", "Not sure yet"];

const FAQ = [
  ["How quickly can a project start?", "Once scope and budget are agreed, we can typically mobilise fast. Share your timeline in the brief and we'll confirm feasibility."],
  ["Do you work with other research agencies?", "Yes. Many partners use us as an operations engine for programming, fieldwork and data processing."],
  ["Which methodologies do you offer?", "Quantitative surveys, qualitative interviews and groups, B2B and expert research, analytics and strategic consultancy."],
  ["How do you price projects?", "Every project is quoted on scope, audience and sample size. We aim for best-in-class quality at competitive pricing."],
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);

const inputCls = "mt-2 w-full border-b border-black/25 bg-transparent py-3 text-base outline-none transition-colors focus:border-[var(--cobalt)]";

const Chips = ({ options, value, onChange, testid }) => (
  <div className="mt-3 flex flex-wrap gap-2">
    {options.map((o) => (
      <button type="button" key={o} onClick={() => onChange(o)} data-testid={`${testid}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        className={`rounded-full border px-4 py-2 text-sm transition-colors ${value === o ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-black/20 hover:border-[var(--ink)]"}`}>
        {o}
      </button>
    ))}
  </div>
);

const EnquiryForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, f);
      setDone(true);
      setF(EMPTY);
      toast.success("Thanks! Your enquiry has been received.");
    } catch {
      toast.error("Please check your name, email and brief, then try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[4px] bg-[var(--ink)] p-10 text-white sm:p-14" data-testid="enquiry-success">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-[var(--cobalt)]"><Check className="h-6 w-6" /></span>
        <h3 className="mt-8 text-4xl font-semibold tracking-tight">Brief received.</h3>
        <p className="mt-3 text-zinc-300">Our team will get back to you shortly with next steps.</p>
        <button onClick={() => setDone(false)} data-testid="enquiry-new-button" className="mt-8 border-b border-white pb-0.5 text-sm">Send another enquiry</button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="enquiry-form">
      <div className="grid gap-8 sm:grid-cols-2">
        <Field label="Your name *"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name-input" /></Field>
        <Field label="Work email *"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="enquiry-email-input" /></Field>
        <Field label="Organisation"><input value={f.company} onChange={set("company")} className={inputCls} data-testid="enquiry-company-input" /></Field>
        <Field label="Target geography"><input value={f.geography} onChange={set("geography")} className={inputCls} data-testid="enquiry-geography-input" /></Field>
      </div>
      <Field label="Research type"><Chips options={SERVICES.map((s) => s.title)} value={f.research_type} onChange={set("research_type")} testid="enquiry-type" /></Field>
      <Field label="Budget range"><Chips options={BUDGETS} value={f.budget} onChange={set("budget")} testid="enquiry-budget" /></Field>
      <Field label="Project brief *"><textarea required rows={4} value={f.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="enquiry-message-input" placeholder="What do you need to know, and why?" /></Field>
      <button disabled={busy} data-testid="enquiry-submit-button" className="group inline-flex items-center gap-3 rounded-full bg-[var(--ink)] py-2 pl-7 pr-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--cobalt)] disabled:opacity-60">
        {busy ? "Sending…" : "Send enquiry"}
        <span className="grid h-10 w-10 place-items-center rounded-full bg-white text-[var(--ink)] transition-transform group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
      </button>
    </form>
  );
};

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <PageHero testid="contact-hero" label="Contact" lines={["Let's build your", <span className="font-serif-i">market advantage.</span>]} intro="Tell us about your research challenge. We'll come back with a clear approach, timeline and competitive quote." />
      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7"><Reveal><EnquiryForm /></Reveal></div>
          <aside className="space-y-4 lg:col-span-4 lg:col-start-9" data-testid="contact-details">
            {[[Mail, "Email", CONTACT.email], [Phone, "Phone", CONTACT.phone], [MapPin, "Office", CONTACT.address]].map(([Icon, l, v], i) => (
              <Reveal key={l} delay={i * 0.08} className="flex gap-4 rounded-[4px] border border-black/10 bg-white p-6">
                <Icon className="mt-0.5 h-5 w-5 text-[var(--cobalt)]" />
                <div><p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">{l}</p><p className="mt-1 font-medium">{v}</p></div>
              </Reveal>
            ))}
          </aside>
        </div>
      </section>
      <section className="mx-auto max-w-[1400px] px-5 pb-28 sm:px-8" data-testid="contact-faq">
        <div className="grid gap-10 lg:grid-cols-12">
          <h2 className="text-4xl font-semibold tracking-[-0.03em] lg:col-span-4">Questions, <span className="font-serif-i">answered.</span></h2>
          <Accordion type="single" collapsible className="lg:col-span-8">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`} className="border-black/15">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left text-lg font-semibold hover:no-underline">{q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-base text-[var(--muted)]">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </main>
  );
}
